/**
 * A screen-space arrow over the tug that points at the current objective.
 *
 * Navigation aids in games get overthought; children need exactly two facts —
 * which way, and how far. This is a Lucide glyph in a DOM overlay rather than
 * scene geometry: a 3D arrow was tried first and read as a yellow blob
 * whenever it pointed into the camera, which for an objective dead ahead was
 * most of the time. A flat icon rotated in screen space has no bad angles.
 *
 * The pointing is done by projecting BOTH the tug and the target to screen
 * coordinates and taking the angle between them — not by rotating a world
 * bearing — so it stays honest whatever the camera is doing. It hides inside
 * the arrival radius so it never nags you through the tricky final berthing.
 */

import { Vector3 } from 'three'
import type { PerspectiveCamera } from 'three'

const HOVER_HEIGHT = 12
const HIDE_WITHIN = 30

const _projected = new Vector3()

export class WaypointArrow {
  private readonly element: HTMLElement
  private readonly icon: HTMLElement
  private readonly label: HTMLElement

  constructor(parent: HTMLElement) {
    this.element = document.createElement('div')
    this.element.className = 'waypoint'
    this.element.hidden = true

    this.icon = document.createElement('span')
    this.icon.className = 'waypoint-icon icon-navigation-2'

    this.label = document.createElement('span')
    this.label.className = 'waypoint-distance'

    this.element.append(this.icon, this.label)
    parent.append(this.element)
  }

  /**
   * Place the arrow above the tug, pointed along the screen-space line to
   * `target`; pass null to hide. Returns the horizontal distance in metres
   * (0 when hidden) so the HUD banner can echo it.
   */
  update(tugPosition: Vector3, target: Vector3 | null, camera: PerspectiveCamera): number {
    if (!target) {
      this.element.hidden = true
      return 0
    }

    const dx = target.x - tugPosition.x
    const dz = target.z - tugPosition.z
    const distance = Math.hypot(dx, dz)

    if (distance < HIDE_WITHIN) {
      this.element.hidden = true
      return distance
    }

    // Anchor point above the wheelhouse, projected to NDC.
    _projected.set(tugPosition.x, tugPosition.y + HOVER_HEIGHT, tugPosition.z).project(camera)
    if (_projected.z > 1) {
      // Behind the camera — the chase cam is mid-swing; skip a frame.
      this.element.hidden = true
      return distance
    }
    const anchorX = (_projected.x * 0.5 + 0.5) * window.innerWidth
    const anchorY = (-_projected.y * 0.5 + 0.5) * window.innerHeight

    // Target at water level, projected the same way. When it is behind the
    // camera the projection mirrors, so flip the delta back.
    _projected.set(target.x, 0, target.z).project(camera)
    const behind = _projected.z > 1 ? -1 : 1
    const targetX = (_projected.x * 0.5 + 0.5) * window.innerWidth
    const targetY = (-_projected.y * 0.5 + 0.5) * window.innerHeight

    const angle = Math.atan2((targetY - anchorY) * behind, (targetX - anchorX) * behind)

    this.element.hidden = false
    this.element.style.transform = `translate(${anchorX}px, ${anchorY}px)`
    // The glyph points up by default; +90° re-bases it onto the screen X axis.
    this.icon.style.transform = `translate(-50%, -100%) rotate(${(angle * 180) / Math.PI + 90}deg)`
    this.label.textContent = distance >= 950 ? `${(distance / 1000).toFixed(1)} km` : `${Math.round(distance / 10) * 10} m`
    return distance
  }
}
